import { CloseFullscreen, InfoOutlined } from "@mui/icons-material";
import { AnimatePresence } from "motion/react";
import { FC, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  MAP_LEGEND_MOTION_TRANSITION,
  MAP_LEGEND_MOTION_VARIANTS,
} from "./collapsible-map-legend.constants";
import {
  MapLegendCollapsedButtonStyled,
  MapLegendPanelStyled,
} from "./collapsible-map-legend.styled";

interface ICollapsibleMapLegend {
  /** Localized legend title shown in the header. */
  title: string;
  /** The legend rows — each typically a `.map-legend__row` span containing a `MapLegendSwatchStyled`. */
  children: ReactNode;
}

/**
 * A map key that collapses into a compact button (so it never blocks the map corner) and expands back on
 * click. Shared by every Leaflet map (live map, ride investigation): callers supply only the title and the
 * rows, keeping the collapse behavior, motion and chrome in one place.
 */
export const CollapsibleMapLegend: FC<ICollapsibleMapLegend> = ({
  title,
  children,
}) => {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(true);

  return (
    <AnimatePresence mode="wait" initial={false}>
      {collapsed ? (
        <MapLegendCollapsedButtonStyled
          key="map-legend-collapsed"
          type="button"
          onClick={() => setCollapsed(false)}
          aria-label={t("rideMap.LEGEND_EXPAND")}
          title={t("rideMap.LEGEND_EXPAND")}
          variants={MAP_LEGEND_MOTION_VARIANTS}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={MAP_LEGEND_MOTION_TRANSITION}
        >
          <InfoOutlined />
        </MapLegendCollapsedButtonStyled>
      ) : (
        <MapLegendPanelStyled
          key="map-legend-expanded"
          variants={MAP_LEGEND_MOTION_VARIANTS}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={MAP_LEGEND_MOTION_TRANSITION}
        >
          <span className="map-legend__header">
            <span className="map-legend__title">
              <InfoOutlined />
              {title}
            </span>
            <button
              type="button"
              className="map-legend__collapse"
              onClick={() => setCollapsed(true)}
              aria-label={t("rideMap.LEGEND_COLLAPSE")}
              title={t("rideMap.LEGEND_COLLAPSE")}
            >
              <CloseFullscreen />
            </button>
          </span>
          {children}
        </MapLegendPanelStyled>
      )}
    </AnimatePresence>
  );
};
